import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getStage,
  updateStage,
  getModels,
  getPromptVersions,
  createPromptVersion,
  activatePromptVersion,
} from '../../api/admin'

export default function StagePage() {
  const { stageId } = useParams<{ stageId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [model, setModel] = useState<string>('')
  const [temperature, setTemperature] = useState<string>('')
  const [maxTokens, setMaxTokens] = useState<string>('')
  const [promptTemplate, setPromptTemplate] = useState('')
  const [changeDescription, setChangeDescription] = useState('')
  const [isActive, setIsActive] = useState(true)

  const { data: stage, isLoading: stageLoading } = useQuery({
    queryKey: ['admin-stage', stageId],
    queryFn: () => getStage(Number(stageId)),
    enabled: !!stageId,
  })

  const { data: models } = useQuery({
    queryKey: ['admin-models'],
    queryFn: getModels,
  })

  const { data: promptVersions } = useQuery({
    queryKey: ['admin-prompts', stageId],
    queryFn: () => getPromptVersions(Number(stageId)),
    enabled: !!stageId,
  })

  // Initialize form when stage loads
  useEffect(() => {
    if (stage) {
      setModel(stage.model || '')
      setTemperature(stage.temperature?.toString() || '')
      setMaxTokens(stage.max_tokens?.toString() || '')
      setPromptTemplate(stage.prompt_template || '')
      setIsActive(stage.is_active)
    }
  }, [stage])

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof updateStage>[1]) =>
      updateStage(Number(stageId), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-stage', stageId] })
      queryClient.invalidateQueries({ queryKey: ['admin-stages'] })
    },
  })

  const createPromptMutation = useMutation({
    mutationFn: (data: { prompt_template: string; change_description?: string; activate?: boolean }) =>
      createPromptVersion(Number(stageId), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-prompts', stageId] })
      queryClient.invalidateQueries({ queryKey: ['admin-stage', stageId] })
      setChangeDescription('')
    },
  })

  const activatePromptMutation = useMutation({
    mutationFn: (version: number) => activatePromptVersion(Number(stageId), version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-prompts', stageId] })
      queryClient.invalidateQueries({ queryKey: ['admin-stage', stageId] })
    },
  })

  const handleSaveSettings = () => {
    updateMutation.mutate({
      model: model || undefined,
      temperature: temperature ? parseFloat(temperature) : undefined,
      max_tokens: maxTokens ? parseInt(maxTokens) : undefined,
      is_active: isActive,
    })
  }

  const handleSavePrompt = () => {
    createPromptMutation.mutate({
      prompt_template: promptTemplate,
      change_description: changeDescription || undefined,
      activate: true,
    })
  }

  const isLLMStage = stage?.queue === 'llm_fast' || stage?.queue === 'llm_quality'

  // Group models by provider
  const modelsByProvider = models?.reduce((acc, m) => {
    const provider = m.id.split('/')[0]
    if (!acc[provider]) acc[provider] = []
    acc[provider].push(m)
    return acc
  }, {} as Record<string, typeof models>)

  if (stageLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-48"></div>
        </div>
      </div>
    )
  }

  if (!stage) {
    return (
      <div className="p-8">
        <p className="text-gray-500">Stage not found</p>
      </div>
    )
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/pipelines')}
          className="text-gray-400 hover:text-gray-600 transition"
        >
          ← Back
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{stage.name}</h1>
          <p className="text-gray-500 text-sm">
            {stage.stage_type} • Queue: {stage.queue}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Settings */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Settings</h2>

          <div className="space-y-4">
            {/* Active toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Active</label>
              <button
                onClick={() => setIsActive(!isActive)}
                className={`relative w-12 h-6 rounded-full transition ${
                  isActive ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition ${
                    isActive ? 'left-7' : 'left-1'
                  }`}
                />
              </button>
            </div>

            {isLLMStage && (
              <>
                {/* Model selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model
                  </label>
                  <select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  >
                    <option value="">Select a model...</option>
                    {modelsByProvider && Object.entries(modelsByProvider).map(([provider, providerModels]) => (
                      <optgroup key={provider} label={provider}>
                        {providerModels?.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name} ({m.context_length.toLocaleString()} ctx)
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                {/* Temperature */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Temperature
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="2"
                    step="0.1"
                    value={temperature}
                    onChange={(e) => setTemperature(e.target.value)}
                    placeholder="0.7"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                </div>

                {/* Max tokens */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Tokens
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100000"
                    value={maxTokens}
                    onChange={(e) => setMaxTokens(e.target.value)}
                    placeholder="4096"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                </div>
              </>
            )}

            <button
              onClick={handleSaveSettings}
              disabled={updateMutation.isPending}
              className="w-full py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
            </button>

            {updateMutation.isSuccess && (
              <p className="text-green-600 text-sm text-center">Settings saved!</p>
            )}
          </div>
        </div>

        {/* Prompt Editor (only for LLM stages) */}
        {isLLMStage && (
          <div className="bg-white rounded-xl shadow p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Prompt Template</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prompt
                </label>
                <textarea
                  value={promptTemplate}
                  onChange={(e) => setPromptTemplate(e.target.value)}
                  rows={12}
                  placeholder="Enter your prompt template here...&#10;&#10;Use {variable} for placeholders."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none font-mono text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Change Description (optional)
                </label>
                <input
                  type="text"
                  value={changeDescription}
                  onChange={(e) => setChangeDescription(e.target.value)}
                  placeholder="e.g., Improved speaker detection accuracy"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                />
              </div>

              <button
                onClick={handleSavePrompt}
                disabled={createPromptMutation.isPending || !promptTemplate.trim()}
                className="w-full py-2 bg-purple-500 hover:bg-purple-600 text-white font-medium rounded-lg transition disabled:opacity-50"
              >
                {createPromptMutation.isPending ? 'Saving...' : 'Save as New Version'}
              </button>

              {createPromptMutation.isSuccess && (
                <p className="text-green-600 text-sm text-center">Prompt saved!</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Prompt History (only for LLM stages) */}
      {isLLMStage && promptVersions && promptVersions.length > 0 && (
        <div className="mt-8 bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Prompt History</h2>

          <div className="space-y-3">
            {promptVersions.map((pv) => (
              <div
                key={pv.id}
                className={`p-4 rounded-lg border ${
                  pv.is_active ? 'border-purple-300 bg-purple-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      pv.is_active ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      v{pv.version}
                    </span>
                    {pv.change_description && (
                      <span className="text-sm text-gray-600">{pv.change_description}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">
                      {new Date(pv.created_at).toLocaleDateString()}
                    </span>
                    {!pv.is_active && (
                      <button
                        onClick={() => activatePromptMutation.mutate(pv.version)}
                        disabled={activatePromptMutation.isPending}
                        className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition"
                      >
                        Activate
                      </button>
                    )}
                    <button
                      onClick={() => setPromptTemplate(pv.prompt_template)}
                      className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition"
                    >
                      Load
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
